const SITE_NAME = 'GotChat';
const REPOSITORY_ORIGIN = 'https://plugins.gotchat.ai';

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, String(value || ''));
  });
  return element;
}

function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function truncate(value, maxLength) {
  const text = cleanText(value);
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1).replace(/\s+\S*$/, '') + '…';
}

export function setPageSeo({
  title,
  description,
  path = '/',
  type = 'website',
  schema = null
}) {
  const finalTitle = truncate(title, 68);
  const finalDescription = truncate(description, 160);
  const normalizedPath = String(path || '/').startsWith('/') ? path : '/' + path;
  const canonicalUrl = REPOSITORY_ORIGIN + normalizedPath;

  document.title = finalTitle;
  upsertMeta('meta[name="description"]', { name: 'description', content: finalDescription });
  upsertMeta('meta[name="robots"]', {
    name: 'robots',
    content: 'index,follow,max-image-preview:large,max-snippet:-1'
  });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: finalTitle });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: finalDescription });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: finalTitle });
  upsertMeta('meta[name="twitter:description"]', {
    name: 'twitter:description',
    content: finalDescription
  });

  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }
  canonical.href = canonicalUrl;

  document.getElementById('initial-structured-data')?.remove();
  document.getElementById('route-structured-data')?.remove();
  if (schema) {
    const script = document.createElement('script');
    script.id = 'route-structured-data';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      ...schema,
      url: canonicalUrl
    });
    document.head.appendChild(script);
  }
}

export function repositorySeo() {
  setPageSeo({
    title: 'Self-Hosted AI Plugins, Workflows & LLM Tools | GotChat',
    description:
      'Find GotChat plugins and workflows for self-hosted AI chat, local LLMs, agents, speech, automation, model management, and custom interfaces.',
    path: '/',
    schema: {
      '@type': 'CollectionPage',
      name: 'GotChat AI Plugin Repository',
      isPartOf: {
        '@type': 'WebSite',
        name: SITE_NAME,
        url: 'https://gotchat.ai/'
      },
      about: [
        'Self-hosted AI plugins',
        'AI chat plugins',
        'Local LLM tools',
        'Agent workflows',
        'Full-stack AI extensions'
      ]
    }
  });
}

export function pluginSeo(plugin) {
  if (!plugin) return;
  const name = cleanText(plugin.name) || 'GotChat Plugin';
  const description =
    cleanText(plugin.readmeParsed?.summary || plugin.description) ||
    'Download a GotChat plugin for self-hosted AI chat, local models, workflows, and custom application capabilities.';
  setPageSeo({
    title: name + ' – Self-Hosted AI Plugin | GotChat',
    description: description + ' Explore versions, requirements, reviews, and download information.',
    path: window.location.pathname,
    type: 'product',
    schema: {
      '@type': 'SoftwareApplication',
      name,
      description,
      applicationCategory: 'DeveloperApplication',
      operatingSystem: 'Windows, macOS, Linux',
      author: plugin.authorName
        ? { '@type': 'Person', name: cleanText(plugin.authorName) }
        : undefined
    }
  });
}

export function workflowSeo(workflow) {
  if (!workflow) return;
  const name = cleanText(workflow.name) || 'GotChat Workflow';
  const description =
    cleanText(workflow.description) ||
    'Download a reusable GotChat AI workflow for agents, skills, automation, and self-hosted model execution.';
  setPageSeo({
    title: name + ' – AI Agent Workflow | GotChat',
    description: description + ' Review included skills, screenshots, versions, and download information.',
    path: window.location.pathname,
    type: 'article',
    schema: {
      '@type': 'CreativeWork',
      name,
      description,
      genre: 'AI agent workflow',
      author: workflow.authorName
        ? { '@type': 'Person', name: cleanText(workflow.authorName) }
        : undefined
    }
  });
}
